import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SpaceStatus, type TSpaceBase } from '@onesource/schemas';
import { useGetMySpaces } from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { DraftBadge } from 'src/components/draft-badge';
import { appKeyToSlug } from 'src/config';

/**
 * Space List Page
 * Shows all parent spaces accessible to the current user
 */
export const SpaceListPage = () => {
  const navigate = useNavigate();
  const { data: spaces, isLoading, error } = useGetMySpaces();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load spaces: {error.message}</Alert>
      </Box>
    );
  }

  if (!spaces || spaces.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">No spaces available. Please contact an administrator for access.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        My Spaces
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Select a space to view its content and interact with the community
      </Typography>

      <Grid container spacing={3} mt={2}>
        {spaces.map((space: TSpaceBase) => (
          <Grid item xs={12} sm={6} md={4} key={space.id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() =>
                space.slug
                  ? navigate({ to: '/programs/$programSlug', params: { programSlug: appKeyToSlug(space.slug) } })
                  : navigate({ to: '/community-hub/spaces/$id', params: { id: space.id } })
              }
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  {space.icon && <Typography variant="h4">{space.icon}</Typography>}
                  <Typography variant="h6">{space.name}</Typography>
                  {/* Only listed for managers — members never see draft spaces. */}
                  {space.status === SpaceStatus.DRAFT && <DraftBadge />}
                </Box>
                {space.description && (
                  <Typography variant="body2" color="text.secondary">
                    {space.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
